/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2023 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2023 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { Box, Typography } from '@mui/material';
import React from 'react';
import { useFormContext } from 'react-hook-form';
import {
  ReactHookFormHiddenInput,
  ReactHookFormTextField,
} from '@opengeoweb/form-fields';
import { AlertBanner, ConfirmationButtons } from '@opengeoweb/shared';
import { Exclamation } from '@opengeoweb/theme';
import type {
  ViewPresetFormValues,
  WorkspaceSupportedComponentTypes,
} from '../../store/viewPresets/types';
import { PresetAction } from '../../store/viewPresets/types';
import { emptyViewPreset } from '../../store/viewPresets/utils';
import { useWorkspaceTranslation } from '../../utils/i18n';
import PresetLabelsPanelWrapper from '../LabelsPanel/PresetsLabelsPanelWrapper';

const validateTitle = (value: string): boolean | string => {
  if (!value) {
    return true;
  }
  // TODO: use translation from form-fields https://gitlab.com/opengeoweb/opengeoweb/-/issues/4240
  return !value.trim().length ? 'This field is required' : true;
};

export const SHORT_DESC_LIMIT = 100;

export interface ViewPresetsFormProps {
  viewComponentType: WorkspaceSupportedComponentTypes;
  error?: string;
  onSubmit?: () => void;
  action: PresetAction;
  formValues: ViewPresetFormValues;
  isLoading?: boolean;
  showInlineActions?: boolean;
  confirmLabel?: string;
  cancelLabel?: string;
  onClose?: () => void;
  dialogTitle?: string;
}

const ViewPresetsForm: React.FC<ViewPresetsFormProps> = ({
  viewComponentType,
  error,
  onSubmit,
  action,
  formValues,
  isLoading = false,
  showInlineActions = false,
  confirmLabel,
  cancelLabel,
  onClose,
  dialogTitle,
}: ViewPresetsFormProps) => {
  const fieldName = 'title';
  const { t } = useWorkspaceTranslation();
  const { watch } = useFormContext();

  const { title, description, ...otherFormValues } = {
    ...emptyViewPreset(viewComponentType),
    ...formValues,
  };
  const hiddenFields = Object.keys(otherFormValues).map((name) => ({
    name,
    value: otherFormValues[name as keyof typeof otherFormValues],
  }));

  const descriptionValue = watch('description') ?? '';
  const remaining = Math.max(0, SHORT_DESC_LIMIT - descriptionValue.length);

  React.useEffect(() => {
    // autoFocus
    const field = document.querySelector(
      `[name="${fieldName}"]`,
    ) as HTMLInputElement;
    field?.focus();
  }, []);

  const onSubmitForm = (event: React.SubmitEvent<HTMLFormElement>): void => {
    event.preventDefault();
    onSubmit!();
  };

  const shouldShowLabelsForm =
    action === PresetAction.SAVE_AS || action === PresetAction.EDIT;

  const marginBottom = 2;

  return (
    <>
      {Boolean(error) && <AlertBanner title={error!} />}
      {(action === PresetAction.SAVE_AS || action === PresetAction.EDIT) && (
        <form
          data-testid="map-preset-form"
          onSubmit={onSubmitForm}
          style={{ height: '100%' }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              height: '100%',
              containerType: 'inline-size',
            }}
          >
            <Box
              sx={{
                flex: 1,
                minHeight: 360,
                overflow: 'hidden',
                display: 'grid',
                gridTemplateColumns: '1fr',
                ...(shouldShowLabelsForm && {
                  '@container (min-width: 610px)': {
                    gridTemplateColumns: '300px 1fr',
                  },
                }),
                alignItems: 'stretch',
              }}
            >
              {!!shouldShowLabelsForm && (
                <Box
                  sx={{
                    minWidth: 0,
                    height: '100%',
                    maxHeight: 200,
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    bgcolor: 'geowebColors.background.surface',
                    maxWidth: '100%',
                    '@container (min-width: 610px)': {
                      pr: 2,
                      minWidth: 288,
                      maxHeight: 'none',
                    },
                  }}
                >
                  <PresetLabelsPanelWrapper
                    viewComponentType={viewComponentType}
                  />
                </Box>
              )}

              <Box
                sx={{
                  pl: 2,
                  pr: 2,
                  py: 2,
                  minWidth: 0,
                  overflow: 'auto',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                {!!dialogTitle && !!shouldShowLabelsForm && (
                  <Typography
                    sx={{ mb: 2, fontWeight: 500 }}
                    variant="subtitle1"
                  >
                    {dialogTitle}
                  </Typography>
                )}

                <Box sx={{ mb: 2 }}>
                  <ReactHookFormTextField
                    defaultValue={title}
                    label={t('workspace-mappreset-dialog-title')}
                    name="title"
                    rules={{
                      required: true,
                      validate: {
                        validateTitle,
                      },
                    }}
                    sx={{ mb: marginBottom }}
                  />
                </Box>

                <Box sx={{ mb: 2 }}>
                  <ReactHookFormTextField
                    defaultValue={description}
                    label={t('workspace-dialog-description')}
                    multiline
                    name="description"
                    rows={3}
                    rules={{ required: false, maxLength: SHORT_DESC_LIMIT }}
                  />
                  <Typography
                    component="div"
                    variant="caption"
                    sx={{
                      marginBottom: 2,
                      marginLeft: 1,
                      marginTop: 1,
                    }}
                  >
                    <Typography
                      color="textSecondary"
                      component="span"
                      variant="inherit"
                    >
                      {t('workspace-char-limit')}{' '}
                    </Typography>
                    <Typography
                      color={remaining === 0 ? 'error' : 'primary'}
                      component="span"
                      variant="inherit"
                    >
                      {remaining}
                    </Typography>
                  </Typography>

                  <ReactHookFormTextField
                    label={t('workspace-dialog-abstract')}
                    multiline
                    name="abstract"
                    rows={6}
                    rules={{ required: false }}
                  />
                </Box>

                {!!showInlineActions && (
                  <Box
                    sx={{
                      mt: 'auto',
                      display: 'flex',
                      justifyContent: 'flex-end',
                      gap: 1,
                      '& > button, & > .MuiBox-root': {
                        minWidth: '0 !important',
                        width: 'auto !important',
                        flexShrink: 1,
                        marginLeft: '0 !important',
                        marginTop: '0 !important',
                        paddingInline: 6,
                      },
                    }}
                  >
                    <ConfirmationButtons
                      cancelLabel={cancelLabel ?? ''}
                      confirmLabel={confirmLabel ?? ''}
                      isLoading={isLoading}
                      onClose={onClose!}
                      onSubmit={onSubmit!}
                    />
                  </Box>
                )}
              </Box>
            </Box>

            {hiddenFields.map(({ name, value }) => (
              <ReactHookFormHiddenInput
                data-testid={name}
                defaultValue={value}
                key={name}
                name={name}
              />
            ))}
          </Box>
        </form>
      )}
      {action === PresetAction.DELETE && (
        <>
          <Typography variant="body1">
            {t('workspace-mappreset-delete-message', { TITLE: title })}
          </Typography>
          <ReactHookFormHiddenInput
            defaultValue={title}
            key="title"
            name="title"
          />
        </>
      )}
      {action === PresetAction.RESET && (
        <Box>
          <Typography variant="body1">
            <Exclamation
              sx={{
                top: '6px',
                position: 'relative',
                color: 'geowebColors.functional.warning',
              }}
            />
            {t('workspace-dialog-confirm-reset-view-message')}
          </Typography>
          <ReactHookFormHiddenInput
            defaultValue={title}
            key="title"
            name="title"
          />
        </Box>
      )}
    </>
  );
};

export default ViewPresetsForm;
